import { useCurrentUser } from '@/contexts/current-user-context';
import { Link, useLocation } from '@/util/react-router-dom';
import { Button } from '@/components/button';
import { NavItem } from './nav-item';
import { useTranslation } from '@/contexts/i18n-context';
import { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './header.module.css';
import { MaterialSymbolIcon } from '../material-symbol-link';
import clsx from 'clsx';
import { useMediaQuery } from '@/hooks/use-media-query';
import { AppMenuKey, getNavigationConfig } from './utils';
import { createPortal } from 'react-dom';
import { AppLogo } from '../app-logo';

interface HeaderProps {
  simplified?: boolean;
  hideVisitButton?: boolean;
}

interface HeaderBackButtonProps {
  to: string;
}

const portalId = 'header-back-button-portal';

export function HeaderBackButton({ to }: HeaderBackButtonProps) {
  return createPortal(
    <Button className={styles.backButton} as="a" to={to} variant="textOnly">
      <MaterialSymbolIcon name={'arrow_back_ios'} className={styles.icon} />
    </Button>,
    // If the element is missing it would cause an error and this is a developer error
    document.getElementById(portalId)!,
  );
}

export function Header({ simplified, hideVisitButton }: HeaderProps) {
  const { user } = useCurrentUser();
  const showMenus = !simplified && user && user.isPreTfa !== 'true';
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isMobile = useMediaQuery('--screen-md');
  const location = useLocation();

  const navigationConfig = useMemo(() => getNavigationConfig(isMobile), [isMobile]);

  const isActive = useCallback(
    (key: AppMenuKey) =>
      navigationConfig.menus[key].items.some(
        (item) => item.type === 'link' && location.pathname === item.to,
      ),
    [navigationConfig, location.pathname],
  );

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        {showMenus && (
          <Button
            variant="textOnly"
            className={styles.burgerMenu}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('header.toggleNavigationMenu')}
          >
            <MaterialSymbolIcon name={isMobileMenuOpen ? 'close' : 'menu'} />
          </Button>
        )}

        <div id={portalId}></div>

        <Link to="/" className={styles.logo}>
          <AppLogo />
        </Link>

        {showMenus && (
          <nav className={clsx(styles.nav, isMobileMenuOpen && styles.navOpen)}>
            <div className={styles.navContent}>
              <div className={styles.navLinks}>
                <NavItem isActive={location.pathname === '/'}>
                  <Link
                    to="/"
                    className={styles.navLink}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('header.dashboard')}
                  </Link>
                </NavItem>
                {navigationConfig.menuOrder.map((menuKey) => (
                  <NavItem
                    key={menuKey}
                    popupProps={{
                      items: navigationConfig.menus[menuKey].items,
                      onItemClick: () => setIsMobileMenuOpen(false),
                    }}
                    isActive={!isMobile && isActive(menuKey)}
                  >
                    {navigationConfig.menus[menuKey].title &&
                      (typeof navigationConfig.menus[menuKey].title === 'string' ? (
                        t(navigationConfig.menus[menuKey].title)
                      ) : (
                        <>
                          <MaterialSymbolIcon name={navigationConfig.menus[menuKey].title.icon} />
                          {t(navigationConfig.menus[menuKey].title.label)}
                        </>
                      ))}
                  </NavItem>
                ))}
              </div>
              {!hideVisitButton && (
                <Button
                  as="a"
                  className={clsx(isMobile ? 'body-2' : 'body-3', styles.startVisitBtn)}
                  to="/visit/policies-consent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('header.startVisit')}
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
